import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { ColorSwatch } from "@/components/ColorPicker";
import { minutesToLabel, formatDuration, todayStr } from "@/lib/timeUtils";
import { ICON_NAMES, Icon } from "@/lib/icons";
import { Trash2, Clock, MapPin, Repeat, Bell, Tag as TagIcon, X } from "lucide-react";
import { motion } from "framer-motion";

const RECUR = [
  { v: "none", l: "Does not repeat" }, { v: "daily", l: "Every day" },
  { v: "weekly", l: "Every week" }, { v: "weekdays", l: "Every weekday" },
  { v: "monthly", l: "Every month" }, { v: "weekends", l: "Every weekend" }, { v: "custom", l: "Custom days" },
];
const REMIND = [
  { v: "", l: "No reminder" }, { v: "5", l: "5 min before" }, { v: "10", l: "10 min before" },
  { v: "30", l: "30 min before" }, { v: "60", l: "1 hour before" }, { v: "1440", l: "1 day before" },
];

function TimeField({ label, minutes, onChange, format }) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return (
    <div className="flex-1">
      <label className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</label>
      <div className="flex gap-1 mt-1">
        <select value={h} onChange={(e) => onChange(Number(e.target.value) * 60 + m)}
          className="flex-1 rounded-lg px-2 py-2 text-sm outline-none tnum" style={{ background: "var(--bg-timeline-track)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)" }}>
          {Array.from({ length: 24 }).map((_, i) => <option key={i} value={i}>{minutesToLabel(i * 60, format).replace(":00", "")}</option>)}
        </select>
        <select value={m} onChange={(e) => onChange(h * 60 + Number(e.target.value))}
          className="w-20 rounded-lg px-2 py-2 text-sm outline-none tnum" style={{ background: "var(--bg-timeline-track)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)" }}>
          {[0, 15, 30, 45].map((x) => <option key={x} value={x}>:{String(x).padStart(2, "0")}</option>)}
        </select>
      </div>
    </div>
  );
}

export function EventModal({ open, onClose, initial, onSaved }) {
  const { categories, eventsApi, settings } = useApp();
  const format = settings?.time_format || "12";
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (initial) setForm({
      title: "", description: "", location: "", notes: "", icon: null,
      category_id: categories[0]?.id || null, color: null, recurrence: "none", reminder: null, recurrence_days: [],
      date: todayStr(), start_minutes: 540, end_minutes: 600, ...initial,
      date: initial.date || todayStr(),
    });
  }, [initial, categories]);

  if (!form) return null;
  const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));
  const isEdit = !!form.id;
  const duration = form.end_minutes - form.start_minutes;

  const save = async () => {
    if (!form.title.trim()) return;
    const payload = { ...form, reminder: form.reminder ? Number(form.reminder) : null };
    if (isEdit) await eventsApi.update(form.id, payload);
    else await eventsApi.add(payload);
    onSaved?.();
    onClose();
  };
  const del = async () => { if (isEdit) await eventsApi.remove(form.id); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0 border-0 gap-0 overflow-hidden" style={{ background: "transparent", boxShadow: "none" }}>
        <DialogTitle className="sr-only">{isEdit ? "Edit Event" : "New Event"}</DialogTitle>
        <DialogDescription className="sr-only">Create or edit a calendar event</DialogDescription>
        <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 320, damping: 26 }}
          className="rounded-3xl overflow-hidden" style={{ background: "var(--bg-sidebar)", border: "1px solid var(--border-subtle)" }} data-testid="event-modal">
          <div className="h-1.5" style={{ background: form.color || categories.find((c) => c.id === form.category_id)?.color || "var(--accent-primary)" }} />
          <div className="p-5 max-h-[78vh] overflow-y-auto space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold" style={{ color: "var(--text-primary)" }}>{isEdit ? "Edit Event" : "New Event"}</h3>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70" style={{ color: "var(--text-muted)" }}><X size={18} /></button>
            </div>

            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Event title" autoFocus
              className="text-base h-11 rounded-xl" style={{ background: "var(--bg-timeline-track)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} data-testid="event-form-title" />

            <div className="flex items-center gap-2 text-sm rounded-xl px-3 py-2" style={{ background: "var(--bg-timeline-track)", color: "var(--text-secondary)" }}>
              <Clock size={15} style={{ color: "var(--accent-primary)" }} />
              <span className="tnum">{minutesToLabel(form.start_minutes, format)} – {minutesToLabel(form.end_minutes, format)}</span>
              <span className="ml-auto font-mono text-xs px-2 py-0.5 rounded-md" style={{ background: "var(--bg-card)", color: "var(--text-muted)" }}>{formatDuration(Math.max(0, duration))}</span>
            </div>

            <div className="flex gap-2">
              <TimeField label="Start" minutes={form.start_minutes} format={format}
                onChange={(v) => setForm((f) => ({ ...f, start_minutes: v, end_minutes: Math.max(v + 15, f.end_minutes) }))} />
              <TimeField label="End" minutes={form.end_minutes} format={format}
                onChange={(v) => setForm((f) => ({ ...f, end_minutes: Math.max(f.start_minutes + 15, v) }))} />
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-wider flex items-center gap-1 mb-1.5" style={{ color: "var(--text-muted)" }}><TagIcon size={12} /> Category</label>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button key={c.id} onClick={() => set("category_id", c.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                    style={{ background: form.category_id === c.id ? c.color : "var(--bg-timeline-track)", color: form.category_id === c.id ? "#fff" : "var(--text-secondary)", border: `1px solid ${form.category_id === c.id ? c.color : "var(--border-subtle)"}` }}
                    data-testid={`event-cat-${c.id}`}>
                    <Icon name={c.icon} size={13} /> {c.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider mb-1.5 block" style={{ color: "var(--text-muted)" }}>Custom Color</label>
                <ColorSwatch color={form.color || categories.find((c) => c.id === form.category_id)?.color || "#3B82F6"} onChange={(v) => set("color", v)} testId="event-color-swatch" />
                {form.color && <button onClick={() => set("color", null)} className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>Use category color</button>}
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider mb-1.5 block" style={{ color: "var(--text-muted)" }}>Icon</label>
                <select value={form.icon || ""} onChange={(e) => set("icon", e.target.value || null)}
                  className="w-full rounded-xl px-3 py-2.5 text-sm outline-none" style={{ background: "var(--bg-timeline-track)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)" }}>
                  <option value="">None</option>
                  {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>

            <div className="relative">
              <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
              <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Location"
                className="pl-9 rounded-xl" style={{ background: "var(--bg-timeline-track)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} />
            </div>

            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notes & description..." rows={2}
              className="rounded-xl resize-none" style={{ background: "var(--bg-timeline-track)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider flex items-center gap-1 mb-1.5" style={{ color: "var(--text-muted)" }}><Repeat size={12} /> Repeat</label>
                <select value={form.recurrence} onChange={(e) => set("recurrence", e.target.value)}
                  className="w-full rounded-xl px-3 py-2.5 text-sm outline-none" style={{ background: "var(--bg-timeline-track)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)" }} data-testid="event-recurrence">
                  {RECUR.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider flex items-center gap-1 mb-1.5" style={{ color: "var(--text-muted)" }}><Bell size={12} /> Reminder</label>
                <select value={form.reminder || ""} onChange={(e) => set("reminder", e.target.value || null)}
                  className="w-full rounded-xl px-3 py-2.5 text-sm outline-none" style={{ background: "var(--bg-timeline-track)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)" }}>
                  {REMIND.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                </select>
              </div>{form.recurrence === "custom" && (<div className="col-span-2"><label className="text-[11px] uppercase tracking-wider mb-1.5 block" style={{ color: "var(--text-muted)" }}>Repeat On</label><div className="flex gap-1.5">{["S","M","T","W","T","F","S"].map((d, i) => (<button key={i} type="button" onClick={() => { const days = form.recurrence_days || []; const next = days.includes(i) ? days.filter((x) => x !== i) : [...days, i]; set("recurrence_days", next); }} className="w-9 h-9 rounded-full text-xs font-semibold" style={{ background: (form.recurrence_days || []).includes(i) ? "var(--accent-primary)" : "var(--bg-timeline-track)", color: (form.recurrence_days || []).includes(i) ? "#fff" : "var(--text-secondary)", border: "1px solid var(--border-subtle)" }}>{d}</button>))}</div></div>)}
            </div>

            <div className="flex items-center gap-2 pt-1">
              {isEdit && (
                <Button variant="ghost" onClick={del} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid="event-delete-btn">
                  <Trash2 size={16} /> Delete
                </Button>
              )}
              <div className="ml-auto flex gap-2">
                <Button variant="ghost" onClick={onClose} style={{ color: "var(--text-secondary)" }}>Cancel</Button>
                <Button onClick={save} className="text-white font-semibold px-6" style={{ background: "var(--accent-primary)" }} data-testid="event-form-submit">
                  {isEdit ? "Save" : "Create"}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
